import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardBody, CardHeader, Field, Input, Modal, ModalFooter, Select, StatusPill, Textarea } from '~/components/ui';
import { supabase } from '~/lib/supabase';
import { useAuth } from '~/lib/auth';
import { adminCreate, adminDelete } from '~/lib/api/admin';
import { formatDateTime } from '~/lib/format';

export const Route = createFileRoute('/_app/admin/playbook')({
  component: PlaybookAdmin,
});

const DASHBOARDS_LIST = [
  { key: 'operations', label: 'Operations' },
  { key: 'rebuild', label: 'Rebuild' },
  { key: 'collections', label: 'Collections' },
  { key: 'sales', label: 'Sales' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'company-overview', label: 'Company' },
];

interface Page {
  id: string; dashboard_key: string; slug: string; title: string;
  body: string | null; sort_order: number; is_published: boolean;
  updated_at: string;
}

function PlaybookAdmin() {
  const auth = useAuth();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<string>('');
  const [editing, setEditing] = useState<Page | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: pages, isLoading } = useQuery({
    queryKey: ['admin_playbook_pages', filter],
    queryFn: async () => {
      let q = supabase.from('playbook_pages').select('*').order('dashboard_key').order('sort_order');
      if (filter) q = q.eq('dashboard_key', filter);
      const { data } = await q;
      return (data ?? []) as Page[];
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminDelete('playbook_pages', id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin_playbook_pages'] }),
  });

  if (!auth.hasRole('admin')) {
    return (
      <div className="px-6 py-6">
        <Card><CardBody>Admin only.</CardBody></Card>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--color-navy)]">Playbook Editor</h1>
          <p className="text-sm text-[var(--color-ink-soft)] mt-1">
            Edit per-dashboard playbook content. Pages render in order on each dashboard's Playbook tab.
          </p>
        </div>
        <Button variant="primary" onClick={() => setCreating(true)}>+ New page</Button>
      </div>

      <div className="flex items-center gap-2 text-xs">
        <Select className="w-48" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All dashboards</option>
          {DASHBOARDS_LIST.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
        </Select>
      </div>

      <Card>
        <CardHeader>{(pages ?? []).length} page(s)</CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="px-5 py-4 text-sm text-[var(--color-ink-muted)]">Loading…</div>
          ) : (pages ?? []).length === 0 ? (
            <div className="px-5 py-4 text-sm text-[var(--color-ink-muted)]">No pages.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface-1)] text-xs uppercase tracking-wide text-[var(--color-ink-soft)]">
                <tr>
                  <th className="text-left px-4 py-2">Dashboard</th>
                  <th className="text-left px-4 py-2">Title</th>
                  <th className="text-left px-4 py-2">Slug</th>
                  <th className="text-right px-4 py-2 w-16">Order</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Updated</th>
                  <th className="text-right px-4 py-2 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(pages ?? []).map((p) => (
                  <tr key={p.id} className="border-t border-[var(--color-line)]">
                    <td className="px-4 py-2 font-mono text-xs">{p.dashboard_key}</td>
                    <td className="px-4 py-2 font-semibold">{p.title}</td>
                    <td className="px-4 py-2 font-mono text-xs text-[var(--color-ink-muted)]">{p.slug}</td>
                    <td className="px-4 py-2 text-right">{p.sort_order}</td>
                    <td className="px-4 py-2">
                      {p.is_published
                        ? <StatusPill kind="completed">Published</StatusPill>
                        : <StatusPill kind="muted">Draft</StatusPill>}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-xs">{formatDateTime(p.updated_at)}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(p)} className="mr-1">Edit</Button>
                      <Button size="sm" variant="ghost"
                        onClick={() => { if (confirm(`Delete "${p.title}"?`)) remove.mutate(p.id); }}>
                        Del
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {creating && <Editor onClose={() => setCreating(false)} />}
      {editing && <Editor existing={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function Editor({ existing, onClose }: { existing?: Page; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    dashboard_key: existing?.dashboard_key ?? 'operations',
    slug: existing?.slug ?? '',
    title: existing?.title ?? '',
    body: existing?.body ?? '',
    sort_order: existing?.sort_order ?? 10,
    is_published: existing?.is_published ?? true,
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (existing) {
        const { error } = await supabase.from('playbook_pages').update({
          dashboard_key: form.dashboard_key,
          slug: form.slug.trim(),
          title: form.title.trim(),
          body: form.body || null,
          sort_order: form.sort_order,
          is_published: form.is_published,
        }).eq('id', existing.id);
        if (error) throw error;
      } else {
        const r = await adminCreate('playbook_pages', {
          dashboard_key: form.dashboard_key,
          slug: form.slug.trim() || form.title.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          title: form.title.trim(),
          body: form.body || null,
          sort_order: form.sort_order,
          is_published: form.is_published,
        });
        if (!r.ok) throw new Error(r.error ?? 'create failed');
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin_playbook_pages'] }); onClose(); },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal open onClose={onClose} title={existing ? 'Edit page' : 'New page'} size="lg">
      <form onSubmit={(e) => { e.preventDefault(); if (form.title.trim()) save.mutate(); }}>
        <div className="px-6 py-4 space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Dashboard" htmlFor="pb_dk">
              <Select id="pb_dk" required value={form.dashboard_key}
                onChange={(e) => setForm({ ...form, dashboard_key: e.target.value })}>
                {DASHBOARDS_LIST.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
              </Select>
            </Field>
            <Field label="Slug" htmlFor="pb_slug" hint="Auto-derived from title if blank.">
              <Input id="pb_slug" value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })} />
            </Field>
            <Field label="Sort order" htmlFor="pb_so">
              <Input id="pb_so" type="number" value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
            </Field>
          </div>
          <Field label="Title" htmlFor="pb_t">
            <Input id="pb_t" required value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Field>
          <Field label="Body (markdown)" htmlFor="pb_b" hint="Supports # / ## headings, **bold**, `code`, - lists, 1. numbered lists.">
            <Textarea id="pb_b" value={form.body} rows={14}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="font-mono text-xs" />
          </Field>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input type="checkbox" checked={form.is_published}
              onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
            Published
          </label>
          {error && <div className="text-sm text-[var(--color-danger)]">{error}</div>}
        </div>
        <ModalFooter>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
